import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ProgramTypeForm } from "../../../components/program/ProgramTypeForm";
import { useProgramTypeById } from "@/hooks/workflow/useProgramTypeById";
import { useAuth } from "@/contexts/AuthContext";
import { workflowService } from "@/services/features/WorkflowService";
import { toast } from "@/hooks/use-toast";
import LoaderComponent from "@/components/common/LoaderComponent";

const EditProgramType = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, token } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [currentData, setCurrentData] = useState<any>(null);
  
  const { programType, loading, error } = useProgramTypeById(id);

  // Track changes in form data
  useEffect(() => {
    if (programType && currentData) {
      const nameChanged = currentData.programName !== programType.program_type;
      const initialIds = (programType.program_status_mapper_ids || []).map(String).sort();
      const currentIds = currentData.applicableStatusIds.map(String).sort();
      const statusesChanged = JSON.stringify(currentIds) !== JSON.stringify(initialIds);
      setHasChanges(nameChanged || statusesChanged);
    }
  }, [currentData, programType]);

  const handleSubmit = async (data: any) => {
    if (!user?.id || !token || !id) {
      toast({
        title: "Error",
        description: "Authentication required",
        variant: "destructive"
      });
      return;
    }

    if (!hasChanges) {
      toast({
        title: "No Changes",
        description: "No changes were made to the program type.",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Convert string IDs to numbers for the API
      const numericIds = data.applicableStatusIds.map((id: string) => parseInt(id));
      
      await workflowService.updateProgramType(
        user.id,
        id,
        data.programName,
        numericIds,
        token
      );

      toast({
        title: "Success",
        description: "Program type updated successfully.",
      });
      
      navigate("/admin/program-type");
    } catch (error: any) {
      console.error("Error updating program type:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update program type",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/program-type");
  };

  if (loading) {
    return <LoaderComponent size="lg" />;
  }

  if (error || !programType) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground">Program Type Not Found</h2>
          <p className="text-muted-foreground mt-2">{error || "The requested program type could not be found."}</p>
        </div>
      </div>
    );
  }

  // Transform the data to match the form's expected format
  const initialData = {
    programName: programType.program_type,
    applicableStatusIds: (programType.program_status_mapper_ids || []).map(id => id.toString())
  };

  return (
    <ProgramTypeForm
      initialData={initialData}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isEdit={true}
      isLoading={isSubmitting}
      hasChanges={hasChanges}
      onDataChange={setCurrentData}
    />
  );
};

export default EditProgramType;